/* import __COLOCATED_TEMPLATE__ from './guidance.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import type Settings from 'embercom/models/ai-agent/tone-of-voice-settings';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import {
  TONE_OF_VOICE_SECTION_TITLES,
  ANSWER_LENGTH_SECTION_TITLES,
  type Tone,
  type AnswerLength,
} from 'embercom/components/operator/fin/setup/personality';
import type { GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import { hasFeature } from 'embercom/helpers/has-feature';
import type BannerDismissalService from 'embercom/services/banner-dismissal-service';
import type { AttributeInfoResolver } from '@intercom/interblocks.ts';
import type EscalationRule from 'embercom/models/ai-agent/escalation-rule';
import type RouterService from '@ember/routing/router-service';
import { registerDestructor } from '@ember/destroyable';
import { EntityType } from 'embercom/models/data/entity-types';

export const DEFAULT_MAX_GUIDELINE_COUNT = 100;
export const INCREASED_MAX_GUIDELINE_COUNT = 200;
export const PREVIEW_REFRESH_INTERVAL = 3000;
const SURVEY_ID = 44017774;
const STANDALONE_COPILOT_BANNER = 'standalone-guidance-not-available-in-copilot-banner';

enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

interface CategoryOption {
  id: GuidelineCategory;
  title: string;
  description: string;
  icon: InterfaceIconName;
}

interface Args {
  openSectionId?: string;
  aiContentSegmentIds?: string;
}

export default class AiAgentGuidance extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare bannerDismissalService: BannerDismissalService;
  @service declare attributeService: $TSFixMe;
  @service declare router: RouterService;

  @tracked openSectionId = '';
  @tracked personalityCustomizationSettings?: Settings;
  @tracked previewLocale = 'en';
  @tracked showPreview = true;
  @tracked private hasDismissedStandaloneCopilotBanner = true;
  @tracked searchTerm: string | undefined;
  @tracked selectedCategories: GuidelineCategory[] = [];
  @tracked selectedAiContentSegmentIds: number[] = [];
  @tracked selectedChannels: string[] = [];

  // These hooks are run in the context of the composers when clicking the cancel button
  cancelHooks: (() => void)[] = [];

  get MAX_GUIDELINE_COUNT(): number {
    if (hasFeature('increased-guidance-limit', this.appService)) {
      return INCREASED_MAX_GUIDELINE_COUNT;
    }
    if (hasFeature('fin-guidance-dynamic-limit', this.appService)) {
      return Infinity;
    }
    return DEFAULT_MAX_GUIDELINE_COUNT;
  }
  readonly CHAT_AND_EMAIL_TAB = 'chat-and-email';
  readonly VOICE_TAB = 'voice';
  readonly Entity_Type = EntityType.Guideline;

  @tracked activeTab = this.CHAT_AND_EMAIL_TAB;
  @tracked isVoicePreviewMode = false;
  @tracked preferredChannel: 'chat' | 'email' = 'chat';

  @tracked typingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.getPersonalityCustomizationSettings();
    this.computeStandaloneCopilotBannerVisibility();

    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }

    if (args.openSectionId) {
      this.openSectionId = args.openSectionId;
    }

    if (args.aiContentSegmentIds) {
      this.applyAudienceFilter(args.aiContentSegmentIds);
    }

    this._loadCorrectTab();

    this.router.on('routeDidChange', this._handleRouteChange);

    registerDestructor(this, () => {
      this.router.off('routeDidChange', this._handleRouteChange);
    });
  }

  get guidelineChartDefinitionsHash(): Record<string, Record<Stat, any>> {
    let hash: Record<string, Record<Stat, any>> = {};
    this.guidelines.forEach((guideline) => {
      hash[guideline.id] = guideline.chartDefinitions;
    });
    return hash;
  }

  get escalationRuleChartDefinitionsHash(): Record<string, Record<Stat, any>> {
    let hash: Record<string, Record<Stat, any>> = {};
    this.escalationRules.forEach((escalationRule) => {
      hash[escalationRule.id] = escalationRule.chartDefinitions;
    });
    return hash;
  }

  @action
  _handleRouteChange() {
    this._loadCorrectTab();
  }

  _loadCorrectTab() {
    let currentTabParam = this.showVoiceGuidance
      ? this.router.currentRoute?.queryParams?.tab
      : this.CHAT_AND_EMAIL_TAB;

    if (!currentTabParam) {
      return;
    }

    if (currentTabParam === this.CHAT_AND_EMAIL_TAB) {
      this.activeTab = this.CHAT_AND_EMAIL_TAB;
    } else if (currentTabParam === this.VOICE_TAB) {
      this.activeTab = this.VOICE_TAB;
    }
  }

  @action
  registerCancelHook(hook: () => void) {
    this.cancelHooks.push(hook);
  }

  @action
  unregisterCancelHook(hook: () => void) {
    this.cancelHooks.removeObject(hook);
  }

  async getPersonalityCustomizationSettings(): Promise<$TSFixMe> {
    this.personalityCustomizationSettings = await this.store.findRecord(
      'ai-agent/tone-of-voice-settings',
      this.appService.app.id,
    );
  }

  get guidelines(): Guideline[] {
    return this.store.peekAll('ai-agent/guidelines').toArray();
  }

  get escalationRules(): EscalationRule[] {
    return this.store.peekAll('ai-agent/escalation-rule').toArray();
  }

  get guidelineCount(): number {
    return this.guidelines.filter((guideline) => !guideline.isDeleted).length;
  }

  get hasInvalidGuidelines(): boolean {
    return this.guidelines.some((guideline) => guideline.validations.isInvalid);
  }

  get validationErrors(): string[] {
    return this.guidelines
      .filter((guideline) => guideline.validations.isInvalid)
      .map((guideline) => guideline.validations.messages)
      .flat()
      .uniq();
  }

  get hasUnsavedChanges(): boolean {
    return this.guidelines.some((guideline) => guideline.hasDirtyAttributes || guideline.isDeleted);
  }

  get disableSaveButton(): boolean {
    return this.hasInvalidGuidelines || !this.hasUnsavedChanges;
  }

  get toneOfVoiceSectionTitle(): string {
    return this.personalityCustomizationSettings?.aiToneOfVoice
      ? this.intl.t(
          TONE_OF_VOICE_SECTION_TITLES[this.personalityCustomizationSettings.aiToneOfVoice as Tone],
        )
      : '';
  }

  get answerLengthSectionTitle(): string {
    return this.personalityCustomizationSettings?.aiAnswerLength
      ? this.intl.t(
          ANSWER_LENGTH_SECTION_TITLES[
            this.personalityCustomizationSettings.aiAnswerLength as AnswerLength
          ],
        )
      : '';
  }

  private get chatAndEmailCategories(): CategoryOption[] {
    return [
      {
        id: 'tone',
        title: this.intl.t('ai-agent.guidance.guidelines.categories.tone.title'),
        description: this.intl.t('ai-agent.guidance.guidelines.categories.tone.description'),
        icon: 'messenger',
      },
      {
        id: 'clarification',
        title: this.intl.t('ai-agent.guidance.guidelines.categories.clarification.title'),
        description: this.intl.t(
          'ai-agent.guidance.guidelines.categories.clarification.description',
        ),
        icon: 'help-space',
      },
      ...(hasFeature('fin-escalation-rules', this.appService)
        ? []
        : [
            {
              id: 'routing' as GuidelineCategory,
              title: this.intl.t('ai-agent.guidance.guidelines.categories.routing.title'),
              description: this.intl.t(
                'ai-agent.guidance.guidelines.categories.routing.description',
              ),
              icon: 'new-direct-message' as InterfaceIconName,
            },
          ]),
      {
        id: 'content' as GuidelineCategory,
        title: this.intl.t('ai-agent.guidance.guidelines.categories.content.title'),
        description: this.intl.t('ai-agent.guidance.guidelines.categories.content.description'),
        icon: 'knowledge' as InterfaceIconName,
      },
      {
        id: 'spam_detection' as GuidelineCategory,
        title: this.intl.t('ai-agent.guidance.guidelines.categories.spam-detection.title'),
        description: this.intl.t(
          'ai-agent.guidance.guidelines.categories.spam-detection.description',
        ),
        icon: 'flag' as InterfaceIconName,
      },
      {
        id: 'other',
        title: this.intl.t('ai-agent.guidance.guidelines.categories.other.title'),
        description: this.intl.t('ai-agent.guidance.guidelines.categories.other.description'),
        icon: 'list',
      },
      ...(this.appService.app.canUseCopilotGuidance
        ? [
            {
              id: 'copilot' as GuidelineCategory,
              title: this.intl.t('ai-agent.guidance.guidelines.categories.copilot.title'),
              description: this.intl.t(
                'ai-agent.guidance.guidelines.categories.copilot.description',
              ),
              icon: 'person' as InterfaceIconName,
            },
          ]
        : []),
    ];
  }

  get categories(): CategoryOption[] {
    return this.chatAndEmailCategories;
  }

  get hasContentReadyForFin() {
    return this.aiAgentSetupService.hasContentReadyForFin;
  }

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  private async computeStandaloneCopilotBannerVisibility() {
    this.hasDismissedStandaloneCopilotBanner =
      await this.bannerDismissalService.hasDismissed(STANDALONE_COPILOT_BANNER);
  }

  get canSeeStandaloneCopilotBanner() {
    return (
      this.appService.app.canUseCopilotOnStandalone &&
      !this.appService.app.canUseCopilotGuidance &&
      this.appService.mainStandalonePlatform.hasInstalledCopilot &&
      !this.hasDismissedStandaloneCopilotBanner
    );
  }

  get filteredGuidelines(): Guideline[] {
    let filtered = this.guidelines.filter((guideline) => !guideline.isDeleted);

    // Remove routing guidelines from the filtered list
    // they are shown separately on escalation page
    filtered = filtered.filter((guideline) => guideline.category !== 'routing');

    // Also remove voice_routing guidelines when FF is on
    if (hasFeature('fin-escalation-rules', this.appService)) {
      filtered = filtered.filter((guideline) => guideline.category !== 'voice_routing');
    }

    // Apply category filter (OR logic for multiple categories)
    if (this.selectedCategories.length > 0) {
      filtered = filtered.filter((guideline) =>
        this.selectedCategories.includes(guideline.category),
      );
    }

    // Apply search term filter
    if (this.searchTerm && this.searchTerm.length > 0) {
      filtered = filtered.filter((guideline) => {
        let title = guideline.title?.toLowerCase() || '';
        let prompt = guideline.prompt?.toLowerCase() || '';
        let searchTerm = this.searchTerm!.toLowerCase();

        return title.includes(searchTerm) || prompt.includes(searchTerm);
      });
    }

    // Apply audience filter
    if (this.selectedAiContentSegmentIds.length > 0) {
      filtered = filtered.filter((guideline) =>
        this.selectedAiContentSegmentIds.some((id) => guideline.aiContentSegmentIds.includes(id)),
      );
    }

    // Apply channel filter (when FF is on)
    if (this.selectedChannels.length > 0) {
      filtered = filtered.filter((guideline) => {
        let guidelineChannels = guideline.targetChannels;
        // Guidelines with no target_channels (null/empty) apply to all channels — always show
        if (!guidelineChannels || guidelineChannels.length === 0) {
          return true;
        }
        // Show if guideline's channels overlap with selected filter channels
        return this.selectedChannels.some((ch) => guidelineChannels.includes(ch));
      });
    }

    return filtered;
  }

  get hasSearchResults() {
    return (
      (this.searchTerm && this.searchTerm.length > 0) ||
      this.selectedCategories.length > 0 ||
      this.selectedAiContentSegmentIds.length > 0 ||
      this.selectedChannels.length > 0
    );
  }

  get attributeInfoResolver(): AttributeInfoResolver {
    return this.aiAgentGuidelineService.attributeInfoResolver();
  }

  get showVoiceGuidance() {
    return !this.appService.app.isStandaloneApp || this.appService.app.canUseStandaloneDeployPhone;
  }

  @action
  hideStandaloneCopilotBanner() {
    this.hasDismissedStandaloneCopilotBanner = true;
    this.bannerDismissalService.dismiss(STANDALONE_COPILOT_BANNER);
  }

  async _saveChangedGuidelines(): Promise<void> {
    // .save() by itself is sending requests for all guidelines
    let unsavedGuidelines = this.guidelines.filter((guideline) => guideline.hasDirtyAttributes);

    for (let guideline of unsavedGuidelines) {
      // to ensure new guidelines are created in order
      await guideline.save();
    }
  }

  @dropTask
  *save(): TaskGenerator<void> {
    try {
      yield this._saveChangedGuidelines();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(e);
    }
  }

  @action
  sendPreviewSettingsUpdatedMessage() {
    this.aiAgentGuidelineService.sendPreviewSettingsUpdatedMessage();
  }

  @action
  cancel(): void {
    for (let guideline of this.guidelines) {
      guideline.rollbackAttributes();
    }
    this.cancelHooks.forEach((hook) => hook());
  }

  get userIsTyping(): boolean {
    return !!this.aiAgentGuidelineService.typingTimeout;
  }

  willDestroy() {
    // We need to clear the timeout in case the user navigates away from the page while typing
    // otherwise the message will be sent to the iframe even after the page has been unloaded
    super.willDestroy();
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }

  @action
  startSurvey() {
    startSurvey(SURVEY_ID);
  }

  @action
  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  @action
  togglePreview() {
    this.showPreview = !this.showPreview;
  }

  get currentPreviewChannel(): 'chat' | 'email' | 'voice' {
    return this.isVoicePreviewMode ? 'voice' : this.preferredChannel;
  }

  @action
  switchToPreviewChannel(channel: 'chat' | 'email' | 'voice') {
    if (channel === 'voice') {
      this.isVoicePreviewMode = true;
      this.preferredChannel = 'chat';
    } else {
      this.isVoicePreviewMode = false;
      this.preferredChannel = channel;
    }
  }

  @action
  doSearch(item: any) {
    this.searchTerm = item.target.value.toLowerCase();
  }

  @action
  resetSearch() {
    this.searchTerm = '';
  }

  @action
  onCategoryFilterChange(selectedCategories: GuidelineCategory[]) {
    this.selectedCategories = selectedCategories;
  }

  @action
  onChannelFilterChange(selectedChannels: string[]) {
    this.selectedChannels = selectedChannels;
  }

  @action
  changeTab(tab: string) {
    this.activeTab = tab;
    this.router.transitionTo({ queryParams: { tab } });

    if (tab === this.VOICE_TAB) {
      this.onCategoryFilterChange([]);
    }
  }

  @action
  applyAudienceFilter(filterValue: string) {
    if (filterValue === 'default') {
      this.selectedAiContentSegmentIds = [];
    } else {
      this.selectedAiContentSegmentIds = [Number(filterValue)];
    }
  }

  @action
  onAudienceFilterChange(audienceIds: number[]) {
    this.selectedAiContentSegmentIds = audienceIds;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance': typeof AiAgentGuidance;
  }
}
